import React from 'react';
import { Check, Loader2 } from 'lucide-react';

export const PLAN_FEATURES = [
  'Cold email and SMS campaigns',
  'Prospect search and email verification',
  'Unified inbox for email and SMS replies',
  'Response and delivery tracking',
  'DNC list management with automatic STOP handling',
  'Analytics dashboard',
];

const money = (n) => `$${Number(n).toLocaleString()}`;

export const BillingToggle = ({ billing, onChange }) => (
  <div className="inline-flex items-center bg-gray-200 rounded-xl p-1 gap-1 shadow-inner">
    {['monthly', 'yearly'].map((cycle) => (
      <button
        key={cycle}
        type="button"
        onClick={() => onChange(cycle)}
        className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
          billing === cycle ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-300' : 'text-gray-500 hover:text-gray-800'
        }`}
      >
        {cycle === 'monthly' ? 'Monthly' : 'Yearly (2 months free)'}
      </button>
    ))}
  </div>
);

const PlanCards = ({ plans, billing, onSelect, loadingKey, currentKey, buttonLabel, gridClassName = 'grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch' }) => {
  const yearly = billing === 'yearly';
  return (
    <div className={gridClassName}>
      {plans.map((plan) => {
        const key = `${plan.name}-${billing}`;
        const price = yearly ? plan.price_yearly : plan.price_monthly;
        const isCurrent = currentKey === key;
        const busy = loadingKey === key;
        const featured = !!plan.popular;
        const muted = featured ? 'text-indigo-200' : 'text-gray-500';
        return (
          <div
            key={plan.name}
            className={`relative flex flex-col rounded-2xl p-7 ${
              featured
                ? 'bg-gradient-to-br from-indigo-600 to-violet-700 text-white shadow-2xl shadow-indigo-200/60'
                : 'bg-white text-gray-900 border border-gray-200 shadow-sm'
            }`}
          >
            {featured && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                Most popular
              </span>
            )}
            <h3 className="text-lg font-extrabold">{plan.display_name}</h3>
            <div className="mt-4 flex items-end flex-wrap gap-x-1.5">
              <span className="text-4xl font-extrabold leading-none break-words">{money(price)}</span>
              <span className={`text-sm pb-1 ${muted}`}>{yearly ? '/year' : '/month'}</span>
            </div>
            <p className={`text-sm mt-1.5 min-h-[1.25rem] ${muted}`}>
              {yearly ? `about ${money(Math.round(plan.price_yearly / 12))}/month, 2 months free` : ''}
            </p>
            <p className="mt-5 text-base font-bold">{Number(plan.monthly_credits).toLocaleString()} credits / month</p>
            <p className={`text-xs mt-1 ${muted}`}>Used for prospect searches and email verification</p>
            <ul className="mt-5 mb-7 space-y-2.5 flex-1">
              {PLAN_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <Check size={14} className={`mt-0.5 flex-shrink-0 ${featured ? 'text-white' : 'text-indigo-600'}`} />
                  <span className={featured ? 'text-indigo-50' : 'text-gray-700'}>{f}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => onSelect(plan, billing)}
              disabled={busy || isCurrent || !!loadingKey}
              className={`flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-bold text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                featured ? 'bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg' : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {busy ? (
                <><Loader2 size={16} className="animate-spin" /> Loading...</>
              ) : isCurrent ? (
                'Current plan'
              ) : buttonLabel ? (
                buttonLabel(plan, billing)
              ) : (
                'Get started'
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default PlanCards;
